import type { ZudokuConfig } from "zudoku";
import "./styles.css";

/**
 * Canonical docs origin. Change this if the app is deployed somewhere else,
 * then update public/v1/agent-manifest.json and public/robots.txt to match.
 */
const siteUrl = process.env.DOCS_ORIGIN?.trim() || "https://roamworks.io";
const basePath = process.env.DOCS_BASE_PATH?.trim() || undefined;

const palette = {
  background: "#090B10",
  foreground: "#F4F4F5",
  card: "#11141B",
  cardForeground: "#F4F4F5",
  popover: "#11141B",
  popoverForeground: "#F4F4F5",
  primary: "#C4B5FD",
  primaryForeground: "#090B10",
  secondary: "#11141B",
  secondaryForeground: "#F4F4F5",
  muted: "#11141B",
  mutedForeground: "#98A1B2",
  accent: "#171A24",
  accentForeground: "#F4F4F5",
  destructive: "#F87171",
  destructiveForeground: "#090B10",
  border: "#252A36",
  input: "#252A36",
  ring: "#C4B5FD",
  radius: "0.625rem",
} as const;

const config: ZudokuConfig = {
  basePath,
  canonicalUrlOrigin: siteUrl,
  site: {
    title: "RoamWorks Developer",
    logo: {
      src: {
        light: "/brand/roamworks-wordmark-dark.png",
        dark: "/brand/roamworks-wordmark-dark.png",
      },
      alt: "RoamWorks",
      width: 150,
      height: 25,
      href: "/docs/overview",
    },
    showPoweredBy: false,
    sidebar: {
      collapsible: true,
      toggleVisibility: "always",
      togglePosition: "bottom",
    },
    footer: {
      copyright: "RoamWorks Limited. Documentation built with Zudoku and Pagefind.",
      columns: [
        {
          title: "Documentation",
          links: [
            { label: "Overview", href: "/docs/overview" },
            { label: "Quickstart", href: "/docs/quickstart" },
            { label: "Authentication", href: "/docs/authentication" },
            { label: "Errors", href: "/docs/errors" },
          ],
        },
        {
          title: "For agents",
          links: [
            { label: "Raw API Markdown", href: "/docs/api.md" },
            { label: "Agent prompt", href: "/docs/agent-prompt.md" },
            { label: "OpenAPI document", href: "/docs/openapi/v1.json" },
            { label: "llms.txt", href: "/docs/llms.txt" },
          ],
        },
        {
          title: "Support",
          links: [
            { label: "Partner support", href: "mailto:partners@roamworks.io" },
            { label: "Security", href: "mailto:security@roamworks.io" },
            { label: "Partner dashboard", href: "https://partners.roamworks.io/partners" },
          ],
        },
      ],
    },
  },
  metadata: {
    title: "RoamWorks Partner API",
    description:
      "Developer documentation for the RoamWorks local-eSIM partner API: HMAC authentication, catalogue, quotes, wallet orders, redemption, webhooks and agent controls.",
    favicon: "/favicon.svg",
    applicationName: "RoamWorks Developer",
    keywords: [
      "RoamWorks",
      "partner API",
      "local eSIM",
      "HMAC",
      "agent payments",
      "OpenAPI",
    ],
  },
  header: {
    themeSwitcher: { enabled: false },
    placements: {
      navigation: "start",
      search: "end",
    },
    navigation: [
      { label: "Raw Markdown", to: "/docs/api.md", target: "_blank" },
      { label: "Agent prompt", to: "/docs/agent-prompt.md", target: "_blank" },
    ],
  },
  navigation: [
    {
      type: "category",
      label: "Documentation",
      items: [
        { type: "section", label: "Get started" },
        { type: "doc", file: "docs/overview", label: "Overview" },
        { type: "doc", file: "docs/environments", label: "Environments" },
        { type: "doc", file: "docs/quickstart", label: "Quickstart" },
        { type: "section", label: "Core" },
        { type: "doc", file: "docs/authentication", label: "Authentication" },
        { type: "doc", file: "docs/ip-allowlist", label: "IP allowlist" },
        { type: "doc", file: "docs/limits-and-quotas", label: "Limits and quotas" },
        { type: "doc", file: "docs/errors", label: "Errors" },
        { type: "doc", file: "docs/catalogue", label: "Catalogue" },
        { type: "doc", file: "docs/quotes", label: "Quotes" },
        { type: "doc", file: "docs/orders", label: "Orders" },
        { type: "doc", file: "docs/wallet", label: "Wallet" },
        { type: "doc", file: "docs/webhooks", label: "Webhooks" },
        { type: "section", label: "Agents" },
        { type: "doc", file: "docs/agent-identities", label: "Agent identities" },
        { type: "doc", file: "docs/agent-payment", label: "Agent payment" },
        { type: "doc", file: "docs/approval-flow", label: "Approval flow" },
        { type: "doc", file: "docs/mcp", label: "MCP" },
        { type: "doc", file: "docs/prompt", label: "Prompt" },
        { type: "section", label: "Reference" },
        { type: "link", to: "/api", label: "API reference (OpenAPI)" },
        { type: "link", to: "/docs/api.md", label: "Raw API Markdown", target: "_blank" },
        { type: "link", to: "/docs/agent-prompt.md", label: "Agent prompt (raw)", target: "_blank" },
        { type: "link", to: "/openapi/v1.json", label: "OpenAPI JSON", target: "_blank" },
        { type: "link", to: "/llms.txt", label: "llms.txt", target: "_blank" },
      ],
    },
    { type: "link", to: "/api", label: "API reference" },
  ],
  theme: {
    light: palette,
    dark: palette,
  },
  syntaxHighlighting: {
    themes: {
      light: "github-dark-default",
      dark: "github-dark-default",
    },
  },
  search: {
    type: "pagefind",
    maxSubResults: 4,
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  apis: {
    type: "file",
    input: "./openapi/v1.json",
    path: "/api",
    publish: {
      path: "/openapi/v1.json",
      agentQuality: true,
    },
    options: {
      examplesLanguage: "shell",
      showVersionSelect: "hide",
      expandAllTags: true,
      showInfoPage: true,
      disablePlayground: true,
      disableSecurity: true,
      schemaDownload: {
        enabled: true,
        fileName: "roamworks-partner-v1",
      },
    },
  },
  docs: {
    files: "content/**/*.{md,mdx}",
    publishMarkdown: true,
    contentNegotiation: true,
    defaultOptions: {
      toc: true,
      copyPage: true,
      disablePager: false,
      showLastModified: false,
    },
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
      title: "RoamWorks Partner API",
      description:
        "Developer documentation for the RoamWorks local-eSIM partner API, including HMAC authentication, catalogue, quotes, wallet orders, redemption, webhooks and agent controls.",
      instructions:
        "Use these docs when integrating with or operating a RoamWorks partner integration. Start with Overview and Quickstart, then Authentication, Limits and quotas, and Errors. Read the raw API Markdown for the compact reference and the agent prompt before delegating calls to an autonomous agent. The published OpenAPI document is at /openapi/v1.json.",
    },
  },
  sitemap: {
    siteUrl,
    changefreq: "weekly",
    priority: 0.7,
    autoLastmod: true,
    exclude: ["/404", "/api.md", "/agent-prompt.md"],
  },
  redirects: [{ from: "/", to: "/docs/overview" }, { from: "/api/", to: "/api" }],
  port: 5182,
  enableStatusPages: true,
};

export default config;